import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HardHat, MapPin, ImageSquare, WarningCircle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProgressBar({ value, className = "" }) {
  return (
    <div className={`h-2.5 w-full rounded-full bg-slate-800 overflow-hidden ${className}`}>
      <div className="h-full bg-primary transition-all duration-700" style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/portal/${token}`)
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center px-6 text-center">
      <WarningCircle size={56} weight="fill" className="text-primary mb-4" />
      <h1 className="font-heading font-extrabold text-2xl">Portal no disponible</h1>
      <p className="text-slate-400 mt-2 max-w-sm">Este enlace fue desactivado o ya no es válido. Contacta al administrador de la obra.</p>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">Cargando…</div>;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="client-portal">
      {/* Hero */}
      <header className="border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 text-primary mb-6">
            {data.logo ? (
              <img src={`${BACKEND_URL}${data.logo}`} alt="logo" className="h-10 w-auto object-contain" />
            ) : (
              <>
                <HardHat size={26} weight="fill" />
                <span className="font-heading font-extrabold tracking-tight text-lg">Obra en Control</span>
              </>
            )}
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Portal del cliente · Solo lectura</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl mt-3">{data.title}</h1>
          <div className="mt-8 max-w-md">
            <div className="flex items-end justify-between mb-2">
              <span className="text-slate-400 text-sm">Avance general de obra</span>
              <span className="font-heading font-extrabold text-3xl text-primary" data-testid="portal-overall">{data.overall_progress}%</span>
            </div>
            <ProgressBar value={data.overall_progress} />
            <p className="text-slate-500 text-sm mt-3">{data.contracts_count} frente(s) de trabajo en seguimiento</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-12">
        {/* Frentes / contratos */}
        <section>
          <h2 className="font-heading font-bold text-xl mb-5">Frentes de trabajo</h2>
          {data.contracts.length === 0 ? (
            <p className="text-slate-500 text-sm">Aún no hay frentes de trabajo publicados.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.contracts.map((c, i) => (
                <div key={i} data-testid={`portal-contract-${i}`} className="rounded-md border border-slate-800 bg-slate-900/60 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-white">{c.title}</div>
                      {c.contractor && <div className="text-xs text-slate-500 mt-0.5">{c.contractor}</div>}
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-800 text-slate-300 capitalize">{c.status}</span>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <ProgressBar value={c.progress} />
                    <span className="font-heading font-bold text-sm text-primary w-12 text-right">{c.progress}%</span>
                  </div>
                  {(c.start_date || c.end_date) && (
                    <div className="text-xs text-slate-500 mt-3">
                      {c.start_date || "—"} → {c.end_date || "—"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Galería */}
        <section>
          <div className="flex items-center gap-2 mb-5">
            <ImageSquare size={20} weight="fill" className="text-primary" />
            <h2 className="font-heading font-bold text-xl">Fotografías de avance</h2>
          </div>
          {data.photos.length === 0 ? (
            <p className="text-slate-500 text-sm">Aún no hay fotografías publicadas.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.photos.map((p, i) => (
                <button key={i} onClick={() => setActive(p)} data-testid={`portal-photo-${i}`}
                  className="group relative aspect-square overflow-hidden rounded-md border border-slate-800 bg-slate-900">
                  <img src={`${BACKEND_URL}${p.file}`} alt={p.caption || "avance"} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {p.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-left">
                      <span className="text-[11px] text-white line-clamp-2">{p.caption}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-slate-800 py-8 text-center text-xs text-slate-600">
        Vista de solo lectura · Generada por Obra en Control
      </footer>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="portal-lightbox">
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={`${BACKEND_URL}${active.file}`} alt={active.caption || "avance"} className="w-full max-h-[80vh] object-contain rounded-md" />
            <div className="mt-3 text-center text-sm text-slate-300">
              {active.contract && <span className="text-primary mr-2">{active.contract}</span>}
              {active.caption}
              {active.created_at && <span className="block text-xs text-slate-500 mt-1">{new Date(active.created_at).toLocaleString("es-MX")}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
